import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { cartLineIdentity, normalizeCartLines, type CartLine } from '@shopgnd/shared';

interface CartState {
  lines: CartLine[];
  add: (line: CartLine) => void;
  remove: (productId: string, variantId?: string) => void;
  setQty: (productId: string, quantity: number, variantId?: string) => void;
  clear: () => void;
  totalCents: () => number;
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      lines: [],
      add: (line) => {
        const lines = [...normalizeCartLines(get().lines)];
        const key = cartLineIdentity(line);
        const i = lines.findIndex((l) => cartLineIdentity(l) === key);
        if (i >= 0) lines[i] = { ...lines[i], quantity: lines[i].quantity + line.quantity };
        else lines.push(line);
        set({ lines });
      },
      remove: (productId, variantId) =>
        set({
          lines: normalizeCartLines(get().lines).filter(
            (l) =>
              !(
                l.productId === productId &&
                (variantId == null
                  ? !l.variantId && !l.selectedVariantIds?.length
                  : (l.variantId ?? null) === variantId ||
                    [...(l.selectedVariantIds ?? [])].sort().join(',') === variantId)
              ),
          ),
        }),
      setQty: (productId, quantity, variantId) => {
        const qty = Math.floor(Number(quantity));
        const matchLine = (l: CartLine) => {
          if (l.productId !== productId) return false;
          if (variantId == null) return !l.variantId && !l.selectedVariantIds?.length;
          return (
            (l.variantId ?? null) === variantId ||
            [...(l.selectedVariantIds ?? [])].sort().join(',') === variantId
          );
        };
        if (!Number.isFinite(qty) || qty <= 0) {
          const hit = normalizeCartLines(get().lines).find(matchLine);
          get().remove(productId, variantId ?? hit?.variantId);
          return;
        }
        set({
          lines: normalizeCartLines(get().lines).map((l) =>
            matchLine(l) ? { ...l, quantity: qty } : l,
          ),
        });
      },
      clear: () => set({ lines: [] }),
      totalCents: () =>
        normalizeCartLines(get().lines).reduce((s, l) => s + l.priceCents * l.quantity, 0),
    }),
    {
      name: 'shopgnd-cart',
      merge: (persistedState, currentState) => ({
        ...currentState,
        ...(persistedState as Partial<CartState>),
        lines: normalizeCartLines((persistedState as Partial<CartState> | undefined)?.lines),
      }),
    },
  ),
);
